import { useCalendarPxPerMinute } from "../../store/uiStore";

interface TimeAxisProps {
  /** Pixel height of the tallest day column in the grid */
  heightPx: number;
  /** Today's now-line position and local time, while the trip is under way */
  now?: { top: number; label: string } | null;
}

/** Hour labels this close to the now label are hidden so they don't collide. */
const NOW_LABEL_CLEARANCE_PX = 10;

/**
 * Sticky hour gutter for the time grid. Every column uses its date's local
 * wall clock on the same (zoomable) px/hour scale. Equal Y positions in columns with
 * different zones are intentionally not equal instants.
 */
export function TimeAxis({ heightPx, now = null }: TimeAxisProps) {
  const hours = Array.from({ length: 24 }, (_, hour) => hour);
  const hourHeight = useCalendarPxPerMinute() * 60;

  return (
    <div
      className="sticky left-0 z-10 bg-page relative"
      style={{ height: heightPx, width: 56 }}
      aria-hidden
    >
      {hours.map((hour) => (
        <span
          key={hour}
          className="absolute right-2 -translate-y-1/2 text-2xs font-semibold text-tertiary tabular-nums tracking-wide select-none"
          style={{ top: hour * hourHeight }}
        >
          {hour === 0 ||
          (now &&
            Math.abs(hour * hourHeight - now.top) < NOW_LABEL_CLEARANCE_PX)
            ? ""
            : `${String(hour).padStart(2, "0")}:00`}
        </span>
      ))}
      {now && (
        <span
          className="absolute right-1 -translate-y-1/2 rounded bg-calendar-now px-1 py-px text-2xs font-extrabold text-white tabular-nums select-none"
          style={{ top: now.top }}
          data-testid="now-time-label"
        >
          {now.label}
        </span>
      )}
    </div>
  );
}
